// API client for Cap8 Insight Engine

const API_BASE = import.meta.env.PROD
  ? 'https://cap8-insight-engine.joseph-peteul.workers.dev/api'
  : '/api';

// Session token, stored in localStorage under 'cap8_session'. Attached to
// every request as `Authorization: Bearer <token>`.
const SESSION_KEY = 'cap8_session';
const LEGACY_AUTH_KEY = 'cap8_auth'; // old Basic-auth value - cleared on login

let sessionToken: string | null = null;

/** Store a freshly-issued session token (called after a successful login). */
export function setSession(token: string) {
  sessionToken = token;
  localStorage.setItem(SESSION_KEY, token);
  localStorage.removeItem(LEGACY_AUTH_KEY);
}

export function getAuth(): string | null {
  if (!sessionToken) {
    sessionToken = localStorage.getItem(SESSION_KEY);
  }
  return sessionToken;
}

export function clearAuth() {
  sessionToken = null;
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LEGACY_AUTH_KEY);
}

/**
 * Redirect to /login after clearing any stored session. Used whenever the
 * server tells us our token is no longer valid (401).
 */
function redirectToLogin() {
  clearAuth();
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const auth = getAuth();
  if (!auth) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types
export type ClientStatus = 'live_client' | 'signed_client' | 'prospect_engaged' | 'prospect_early' | 'past';

export interface Client {
  client_id: string;
  name: string;
  archetype: string;
  aum_bucket: string;
  region: string;
  status: ClientStatus;
  status_changed_at: string | null;
  notes: string | null;
}

export interface ClientStatusHistory {
  history_id: string;
  client_id: string;
  old_status: string | null;
  new_status: string;
  changed_at: string;
  changed_by: string | null;
  notes: string | null;
}

// User roles:
// - investor: read-only access to dashboard (no actions), clients, and patterns
// - uploader: can submit transcripts and review extractions
// - founder: can do everything including Founder Gate validation
// - admin: can manage users and system settings
export type UserRole = 'investor' | 'uploader' | 'founder' | 'admin';

export interface User {
  user_id: string;
  name: string;
  email: string;
  role: UserRole;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  // Stats (only populated in listUsers)
  transcripts_uploaded?: number;
  insights_reviewed?: number;
}

export interface Transcript {
  transcript_id: string;
  source: string;
  filename: string | null;
  upload_timestamp: string;
  meeting_date: string;
  client_id: string;
  client_name?: string;
  attendees_cap8: string;
  attendees_client: string;
  meeting_type: string;
  processing_status: string;
  error_message: string | null;
  uploaded_by: string | null;
}

export type Branch = 'engineering' | 'data_science' | 'finance_economics';
export type Disposition = 'pending' | 'linked' | 'rejected';

export interface Insight {
  insight_id: string;
  transcript_id: string;
  client_id: string;
  client_name?: string;
  meeting_date: string;
  insight_text: string;
  supporting_quote: string;
  speaker: string | null;
  speaker_type: 'cap8' | 'client' | 'unknown' | null;
  capability_tag: string | null;
  module_touched: string;
  work_type: string;
  strategy_context: string | null;
  urgency: string;
  sentiment: string;
  is_end_client_report: number;
  extraction_confidence: string;
  feedback_status: string;
  feedback_notes: string | null;
  branch: Branch | null;
  disposition: Disposition;
  rejection_reason: string | null;
  dispositioned_at: string | null;
  dispositioned_by: string | null;
}

export interface MetadataResult {
  meeting_date: string | null;
  meeting_title: string | null;
  attendees: Array<{ name: string; email: string | null; organization: string | null }>;
  suggested_client: { client_id: string; name: string } | null;
  confidence: string;
  source: string;
}

export interface InsightFilters {
  client_id?: string;
  client_ids?: string[];  // Multiple client IDs for multi-select
  date_start?: string;
  date_end?: string;
  capability_tag?: string;
  module_touched?: string;
  work_type?: string;
  urgency?: string;
  feedback_status?: string;
  market_mood?: MarketMood | MarketMood[];
  sort_by?: 'date_desc' | 'date_asc' | 'client' | 'module' | 'urgency' | 'feedback';
  limit?: number;
  offset?: number;
  branch?: Branch;
  disposition?: Disposition;
}

// Citation for insight Q&A - returned by LLM synthesis
export interface InsightCitation {
  marker: number;                    // Citation number [1], [2], etc.
  insight_id: string;               // ins_xxx identifier (mirrors source_id for non-insight sources)
  source_id?: string;               // Canonical cited item ID: ins_/sm_/cmt_/trn_/art_
  source_type?: string;             // insight | strategy_demand | commitment | sentiment | article | transcript
  quote: string;                    // Verbatim quote from insight
  client_name: string;              // Client who gave feedback (publication for articles)
  meeting_date: string;             // When the feedback was given
  urgency: string;                  // Human-readable label (urgency/position/status/sentiment/tone)
  confidence: 'high' | 'medium' | 'low';
}

export interface QueryResult {
  query_id?: string;
  answer: string;
  insight_ids: string[];
  citations?: InsightCitation[];    // Structured citation data with quotes
  query_path: string;
  token_count: number;
  related_questions?: string[];     // Suggested follow-up questions
  auto_filters?: Record<string, unknown>; // Filters inferred from the question text
  sources_used?: string[];          // Labels of data sources that fed the answer
}

// SSE streaming query types
export interface QueryStage {
  key: string;
  label: string;
  [meta: string]: unknown;
}

export interface QueryStreamHandlers {
  onStage?: (stage: QueryStage) => void;
  onDelta?: (text: string) => void;
  onDone?: (result: QueryResult) => void;
  onError?: (message: string) => void;
}

export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface QueryHistory {
  query_id: string;
  query_type: 'point' | 'filtered' | 'large_scope';
  question: string | null;
  filters: string; // JSON
  answer: string | null;
  insight_ids: string | null; // JSON array
  query_path: string | null;
  token_count: number | null;
  execution_time_ms: number | null;
  created_at: string;
  citations?: string | null; // JSON array (present once migration 015 is applied)
}

// CRM Types
export type CommitmentOwner = 'cap8' | 'client' | 'shared' | 'unclear';
export type CommitmentStatus = 'open' | 'done' | 'cancelled' | 'superseded';
export type CommitmentUrgency = 'blocker' | 'high' | 'normal' | 'low' | 'unclear';
export type StandingItemCadence = 'one_time' | 'monthly' | 'quarterly' | 'annually' | 'custom';
export type StandingItemStatus = 'open' | 'done' | 'not_applicable';

export interface Commitment {
  commitment_id: string;
  transcript_id: string;
  client_id: string;
  client_name?: string;
  meeting_date: string;
  commitment_text: string;
  supporting_quote: string;
  speaker: string | null;
  owner: CommitmentOwner;
  due_date: string | null;
  due_date_source: string;
  status: CommitmentStatus;
  status_changed_at: string | null;
  status_changed_by: string | null;
  status_notes: string | null;
  urgency: CommitmentUrgency;
  extraction_confidence: string;
  feedback_status: string;
  feedback_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface StandingItem {
  standing_item_id: string;
  client_id: string;
  client_name?: string;
  item_text: string;
  cadence: StandingItemCadence;
  last_completed_at: string | null;
  status: StandingItemStatus;
  status_changed_at: string | null;
  status_changed_by: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

export interface CommitmentFilters {
  client_id?: string;
  date_start?: string;
  date_end?: string;
  owner?: CommitmentOwner;
  status?: CommitmentStatus;
  urgency?: CommitmentUrgency;
  feedback_status?: string;
  due_date_start?: string;
  due_date_end?: string;
  sort_by?: 'date_desc' | 'date_asc' | 'client' | 'due_date' | 'urgency' | 'status' | 'owner';
  limit?: number;
  offset?: number;
}

export interface CommitmentStats {
  total: number;
  by_status: Array<{ status: string; count: number }>;
  by_owner: Array<{ owner: string; count: number }>;
  by_urgency: Array<{ urgency: string; count: number }>;
}

export interface AttentionNeeded {
  overdue_cap8: Commitment[];
  upcoming_cap8: Commitment[];
  waiting_on_clients: Commitment[];
}

export interface ClientSummary {
  client: Client;
  open_commitments: Commitment[];
  standing_items: StandingItem[];
}

// Loop Closure Types
export type RoadmapModule = 'navigator' | 'studio' | 'quant_engine' | 'dashboard' | 'cross_cutting' | 'platform';
export type RoadmapWorkType = 'r_and_d' | 'engineering' | 'product_ux' | 'mixed';
export type RoadmapHorizon = 'near_term' | 'mid_term' | 'long_term' | 'shipped' | 'deprioritized';
export type RoadmapStatus = 'proposed' | 'active' | 'shipped' | 'deprioritized';
export type NotificationChannel = 'email' | 'meeting' | 'slack' | 'other' | 'not_specified';
export type AdoptionStatus = 'aware' | 'evaluating' | 'adopted' | 'declined' | 'not_applicable';

export interface RoadmapItem {
  roadmap_item_id: string;
  title: string;
  description: string | null;
  module_touched: RoadmapModule;
  work_type: RoadmapWorkType;
  horizon: RoadmapHorizon;
  status: RoadmapStatus;
  target_ship_date: string | null;
  actual_ship_date: string | null;
  owner: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  branch: Branch | null;
}

export interface RoadmapItemWithCounts extends RoadmapItem {
  insight_count: number;
  client_count: number;
  notified_count: number;
  adopted_count: number;
}

export interface RoadmapFilters {
  status?: RoadmapStatus;
  horizon?: RoadmapHorizon;
  module_touched?: RoadmapModule;
  work_type?: RoadmapWorkType;
  owner?: string;
  has_shipped?: boolean;
  target_date_start?: string;
  target_date_end?: string;
  sort_by?: 'status' | 'horizon' | 'target_date' | 'actual_date' | 'title' | 'module' | 'owner';
  limit?: number;
  offset?: number;
  branch?: Branch;
}

export interface InsightRoadmapLink {
  link_id: string;
  insight_id: string;
  roadmap_item_id: string;
  linked_at: string;
  linked_by: string | null;
  link_notes: string | null;
}

export interface ClientNotification {
  notification_id: string;
  roadmap_item_id: string;
  client_id: string;
  notified_at: string;
  notified_by: string | null;
  channel: NotificationChannel;
  notes: string | null;
}

export interface ClientAdoption {
  adoption_id: string;
  roadmap_item_id: string;
  client_id: string;
  adoption_status: AdoptionStatus;
  status_changed_at: string;
  status_changed_by: string | null;
  notes: string | null;
}

export interface FlywheelSummaryItem {
  roadmap_item_id: string;
  title: string;
  actual_ship_date: string;
  insight_count: number;
  client_count: number;
  notified_count: number;
  adopted_count: number;
}

export interface FlywheelSummaryData {
  items: FlywheelSummaryItem[];
  totals: {
    shipped_count: number;
    total_insights: number;
    total_clients: number;
    total_notified: number;
    total_adopted: number;
  };
}

export interface LoopViewData {
  roadmap_item: RoadmapItem;
  linked_insights: Array<Insight & { client_name: string; link_notes: string | null }>;
  client_loop: Array<{
    client_id: string;
    client_name: string;
    insight_count: number;
    last_notification: ClientNotification | null;
    adoption: ClientAdoption | null;
  }>;
}

// Sentiment Types
export type SentimentCap8 = 'negative' | 'neutral' | 'positive' | 'very_positive';
export type SentimentMarket = 'panic' | 'bear' | 'bull' | 'euphoric';
export type SentimentConfidence = 'high' | 'medium' | 'low';

export interface MeetingSentiment {
  transcript_id: string;
  meeting_date: string;
  meeting_type: string;
  attendees_cap8: string;
  attendees_client: string;
  sentiment_cap8: SentimentCap8 | null;
  sentiment_cap8_confidence: SentimentConfidence | null;
  sentiment_cap8_reasoning: string | null;
  sentiment_market: SentimentMarket | null;
  sentiment_market_confidence: SentimentConfidence | null;
  sentiment_market_reasoning: string | null;
}

export interface ClientMoodData {
  client_id: string;
  client_name: string;
  last_meeting_date: string;
  meetings: MeetingSentiment[];
}

export interface MoodGridResponse {
  clients: ClientMoodData[];
  meeting_limit: number;
}

// Market Mood Types
export type MarketMood = 'panic' | 'bear' | 'bull' | 'euphoric' | 'unknown';

export interface MarketMoodPeriod {
  period_id: string;
  start_date: string;
  end_date: string | null;
  mood: SentimentMarket;
  notes: string | null;
  set_by: string;
  created_at: string;
  updated_at: string;
}

export interface MarketMoodDateRange {
  earliest: string | null;
  latest: string | null;
}

export interface MarketMoodCategoryStats {
  mood_distribution: Array<{ market_mood: string; count: number }>;
  date_range: MarketMoodDateRange | null;
}

export interface MarketMoodStats {
  insights: MarketMoodCategoryStats;
  strategy_mentions: MarketMoodCategoryStats;
  article_extractions: MarketMoodCategoryStats;
  total_periods: number;
}

export interface MarketMoodTaggingResult {
  success: boolean;
  total_insights: number;
  tagged_with_mood: number;
  tagged_unknown: number;
  periods_count: number;
  duration_ms: number;
  timestamp: string;
}

// Client Brief Types
export type ClientBriefType = 'internal' | 'shareable';

export interface ClientBrief {
  brief_id: string;
  client_id: string;
  brief_type: ClientBriefType;
  brief_text: string;
  insight_count: number;
  date_range_start: string;
  date_range_end: string;
  created_at: string;
  is_stale: boolean;
  new_insights_since: number;
}

// Article/Market Knowledge Types
export type ArticleDocumentType = 'article' | 'research' | 'speech' | 'earnings_call' | 'policy_doc' | 'other';
export type ArticleProcessingStatus = 'pending' | 'extracting' | 'ready' | 'error';
export type ArticleTone = 'bearish' | 'bullish' | 'cautious' | 'alarmist' | 'measured' | 'contrarian' | 'neutral';
export type ArticleQualityFlag = 'substantive_analysis' | 'news_summary' | 'opinion_piece' | 'data_release' | 'mixed';

export interface Article {
  article_id: string;
  submission_timestamp: string;
  submitted_by: string | null;
  source_paste: string;
  headline: string | null;
  source_publication: string | null;
  author: string | null;
  publication_date: string | null;
  url: string | null;
  document_type: ArticleDocumentType;
  curator_note: string | null;
  processing_status: ArticleProcessingStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields from extraction
  summary?: string | null;
  themes?: string | null;
  extraction_feedback_status?: string | null;
}

export interface ArticleExtraction {
  extraction_id: string;
  article_id: string;
  publication_date: string | null;
  summary: string | null;
  themes: string | null; // JSON array
  asset_classes: string | null; // JSON array
  sectors: string | null; // JSON array
  tickers_mentioned: string | null; // JSON array
  geography: string | null; // JSON array
  time_horizon: string | null;
  market_regime_relevance: string | null; // JSON array
  key_claims: string | null; // JSON array of objects
  advisor_implications: string | null;
  tone: ArticleTone | null;
  sources_cited: string | null; // JSON array
  quality_flag: ArticleQualityFlag | null;
  market_mood: string | null;
  extraction_confidence: string | null;
  feedback_status: string;
  feedback_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ArticlePasteMetadata {
  headline: string | null;
  source_publication: string | null;
  author: string | null;
  publication_date: string | null;
  url: string | null;
  tickers_mentioned: string[];
  confidence: 'high' | 'medium' | 'low';
  tokens_used: number;
}

export interface ArticleFilters {
  source_publication?: string;
  document_type?: ArticleDocumentType;
  date_start?: string;
  date_end?: string;
  processing_status?: ArticleProcessingStatus;
  feedback_status?: string;
  sort_by?: 'submission_desc' | 'submission_asc' | 'publication_desc' | 'publication_asc' | 'source';
  limit?: number;
  offset?: number;
}

// Strategy Demand Types
export type Strategy = 'low_volatility' | 'sp500_tilts' | 'sector_rotation' | 'dynamic_hedge' | 'risk_overlay' | 'core_equity' | 'novel';
export type ClientPosition = 'using_it' | 'considering_it' | 'asking_about_it' | 'pushed_back_on_it' | 'mentioned_in_passing' | 'recommending_against_it';
export type InterestLevel = 'high' | 'medium' | 'low' | 'unclear';
export type Sentiment = 'positive' | 'negative' | 'neutral' | 'mixed';
export type ExtractionConfidence = 'high' | 'medium' | 'low';
export type FeedbackStatus = 'unreviewed' | 'valid' | 'invalid' | 'needs_revision';

export interface StrategyMention {
  mention_id: string;
  transcript_id: string;
  client_id: string;
  client_name?: string;
  meeting_date: string;
  strategy: Strategy;
  strategy_description: string | null;
  client_position: ClientPosition;
  interest_level: InterestLevel;
  is_end_client_demand: boolean;
  sentiment: Sentiment;
  speaker: string | null;
  supporting_quote: string;
  summary: string;
  market_mood: MarketMood;
  market_mood_set_at: string | null;
  extraction_prompt_version: string;
  extraction_confidence: ExtractionConfidence;
  feedback_status: FeedbackStatus;
  feedback_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface StrategyMentionFilters {
  client_id?: string;
  strategy?: Strategy;
  client_position?: ClientPosition;
  interest_level?: InterestLevel;
  is_end_client_demand?: boolean;
  sentiment?: Sentiment;
  market_mood?: MarketMood;
  feedback_status?: FeedbackStatus;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: 'date_desc' | 'date_asc' | 'client' | 'strategy' | 'position' | 'interest' | 'feedback';
  limit?: number;
  offset?: number;
}

export interface StrategyMentionStats {
  total_mentions: number;
  by_strategy: Record<Strategy, number>;
  by_position: Record<ClientPosition, number>;
  by_interest: Record<InterestLevel, number>;
  by_sentiment: Record<Sentiment, number>;
  end_client_demand_count: number;
  by_feedback: Record<FeedbackStatus, number>;
  unique_clients: number;
  date_range: { earliest: string | null; latest: string | null };
}

export interface DemandOverTimePoint {
  period: string;
  strategy: Strategy;
  mention_count: number;
  end_client_count: number;
  advisor_count: number;
}

export interface ClientStrategyLandscape {
  client_id: string;
  client_name: string;
  strategies: Array<{
    strategy: Strategy;
    latest_position: ClientPosition;
    latest_interest: InterestLevel;
    latest_sentiment: Sentiment;
    mention_count: number;
    first_mentioned: string;
    last_mentioned: string;
    has_end_client_demand: boolean;
  }>;
}

// Transcript Query Types (Q&A over transcripts)
export interface TranscriptQueryRequest {
  question: string;
  client_ids: string[];
  date_start?: string;
  date_end?: string;
}

export interface TranscriptQueryCitation {
  transcript_id: string;
  client_name: string;
  meeting_date: string;
  excerpt: string;
}

export interface TranscriptQueryResponse {
  query_id: string;
  answer: string;
  transcript_ids: string[];
  citations: TranscriptQueryCitation[];
  query_path: 'direct_synthesis' | 'haiku_filtered';
  relevance_scores?: Record<string, number>;
  input_tokens: number;
  output_tokens: number;
  execution_time_ms: number;
}

export interface TranscriptQueryHistory {
  query_id: string;
  question: string;
  client_ids: string; // JSON array
  date_start: string | null;
  date_end: string | null;
  transcript_ids: string | null; // JSON array
  answer: string | null;
  citations: string | null; // JSON array
  query_path: string | null;
  relevance_scores: string | null; // JSON object
  input_tokens: number | null;
  output_tokens: number | null;
  execution_time_ms: number | null;
  created_at: string;
}

export interface BadgeCounts {
  triage_pending: number;
  gate_pending: number;
  attention: number;
  degrading_unacked: number;
}

export interface TriageResponse {
  insights: Insight[];
  total: number;
  counts: {
    engineering: number;
    data_science: number;
    finance_economics: number;
    unclassified: number;
  };
}

export interface PulseClient {
  client_id: string;
  client_name: string;
  archetype: string;
  status: string;
  trend: 'improving' | 'stable' | 'degrading';
  trend_score: number;
  degrading_reason: string | null;
  meetings: Array<{
    transcript_id: string;
    meeting_date: string;
    sentiment_cap8: string | null;
    sentiment_cap8_confidence: string | null;
    sentiment_cap8_reasoning: string | null;
  }>;
  open_commitments: number;
  acked: boolean;
  ack_info: { acked_by: string | null; acked_at: string } | null;
  // Per-client flags: 'overdue_cap8' | 'upcoming_cap8' | 'waiting_on_client'
  commitment_attention: string[];
}

export interface PulseRelationshipsData {
  clients: PulseClient[];
  commitment_attention: {
    overdue_cap8: Commitment[];
    upcoming_cap8: Commitment[];
    waiting_on_clients: Commitment[];
  };
}

export interface PulseMarketData {
  meetings: Array<{
    transcript_id: string;
    client_id: string;
    client_name: string;
    meeting_date: string;
    sentiment_market: string | null;
    sentiment_market_confidence: string | null;
  }>;
  articles: Array<{
    article_id: string;
    headline: string | null;
    publication_date: string | null;
    market_mood: string | null;
    tone: string | null;
  }>;
  mood_periods: MarketMoodPeriod[];
}

export interface ClientAsksResponse {
  insights: Array<Insight & {
    linked_roadmap: Array<{
      roadmap_item_id: string;
      title: string;
      status: string;
    }>;
  }>;
  strategy_mentions: StrategyMention[];
}

// API functions
export const api = {
  /**
   * Email + password login. On success, stores the returned session token
   * (Bearer auth for every subsequent request) and returns the user.
   * Throws a generic error on any failure - the server never distinguishes
   * "unknown email" from "wrong password".
   */
  async login(email: string, password: string): Promise<{ user: User }> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Invalid credentials' }));
      throw new Error((error as { error?: string }).error || 'Invalid credentials');
    }

    const data = (await response.json()) as { token: string; user: User };
    setSession(data.token);
    return { user: data.user };
  },

  /** Deletes the server-side session, then clears the local token. */
  async logout(): Promise<void> {
    try {
      await fetchAPI('/auth/logout', { method: 'POST' });
    } finally {
      clearAuth();
    }
  },

  // Get current user
  async getCurrentUser(): Promise<User> {
    return fetchAPI('/me');
  },

  // Clients
  async listClients(): Promise<Client[]> {
    return fetchAPI('/clients');
  },

  async createClient(data: Omit<Client, 'client_id' | 'status_changed_at'>): Promise<Client> {
    return fetchAPI('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateClient(
    clientId: string,
    data: Partial<Pick<Client, 'name' | 'archetype' | 'aum_bucket' | 'region' | 'status' | 'notes'>>
  ): Promise<Client> {
    return fetchAPI(`/clients/${clientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Transcripts
  async listTranscripts(params?: {
    client_id?: string;
    status?: string;
    limit?: number;
  }): Promise<Transcript[]> {
    const searchParams = new URLSearchParams();
    if (params?.client_id) searchParams.set('client_id', params.client_id);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return fetchAPI(`/transcripts${query ? `?${query}` : ''}`);
  },

  async getTranscript(id: string): Promise<Transcript> {
    return fetchAPI(`/transcripts/${id}`);
  },

  async deleteTranscript(id: string): Promise<{ message: string; deleted: { transcript_id: string; insights: number; commitments: number; strategy_mentions: number } }> {
    return fetchAPI(`/transcripts/${id}`, { method: 'DELETE' });
  },

  async extractMetadata(text: string): Promise<MetadataResult> {
    return fetchAPI('/transcripts/extract-metadata', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async submitTranscript(data: {
    transcript: string;
    meeting_date: string;
    client_id: string;
    attendees_cap8: string[];
    attendees_client: string[];
    meeting_type: string;
    source: 'pasted' | 'uploaded_file';
    filename?: string;
  }): Promise<{ transcript: Transcript; message: string }> {
    return fetchAPI('/transcripts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async processTranscript(id: string): Promise<{
    message: string;
    insights_count: number;
    rejected_count: number;
  }> {
    return fetchAPI(`/transcripts/${id}/process`, { method: 'POST' });
  },

  async getTranscriptContent(
    id: string,
    type: 'raw' | 'normalized'
  ): Promise<{ content: string }> {
    return fetchAPI(`/transcripts/${id}/${type}`);
  },

  /**
   * Stream a non-destructive preview of merging two same-meeting Granola
   * recordings. Calls onDelta as the reconstructed transcript is generated.
   */
  async mergeTranscriptPreview(
    transcriptIdA: string,
    transcriptIdB: string,
    handlers: { onDelta?: (text: string) => void; onDone?: (merged: string) => void; onError?: (message: string) => void }
  ): Promise<void> {
    const auth = getAuth();
    if (!auth) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/transcripts/merge-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify({ transcript_id_a: transcriptIdA, transcript_id_b: transcriptIdB }),
    });
    if (response.status === 401) {
      redirectToLogin();
      throw new Error('Session expired');
    }
    if (!response.ok || !response.body) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((err as { error?: string }).error || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of raw.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (!dataLines.length) continue;
        let data: { text?: string; merged?: string; message?: string };
        try { data = JSON.parse(dataLines.join('\n')); } catch { continue; }
        if (event === 'delta' && data.text) handlers.onDelta?.(data.text);
        else if (event === 'done') handlers.onDone?.(data.merged || '');
        else if (event === 'error') handlers.onError?.(data.message || 'Merge failed');
      }
    }
  },

  /**
   * Commit a merge: queues reconstruction + extraction and archives the two
   * source recordings. Returns the new merged transcript id.
   */
  async commitTranscriptMerge(
    transcriptIdA: string,
    transcriptIdB: string,
    meetingDate?: string
  ): Promise<{ transcript_id: string; status: string; meeting_date: string; message: string }> {
    return fetchAPI('/transcripts/merge', {
      method: 'POST',
      body: JSON.stringify({ transcript_id_a: transcriptIdA, transcript_id_b: transcriptIdB, meeting_date: meetingDate }),
    });
  },

  async rerunExtraction(id: string): Promise<{
    message: string;
    insights_count: number;
  }> {
    return fetchAPI(`/transcripts/${id}/rerun-extraction`, { method: 'POST' });
  },

  // Insights
  async listInsights(
    filters: InsightFilters
  ): Promise<{ insights: Insight[]; total: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchAPI(`/insights${query ? `?${query}` : ''}`);
  },

  async getInsight(id: string): Promise<Insight> {
    return fetchAPI(`/insights/${id}`);
  },

  async getInsightsByTranscript(transcriptId: string): Promise<Insight[]> {
    return fetchAPI(`/transcripts/${transcriptId}/insights`);
  },

  async updateFeedback(
    insightId: string,
    feedback_status: string,
    feedback_notes?: string
  ): Promise<Insight> {
    return fetchAPI(`/insights/${insightId}/feedback`, {
      method: 'PUT',
      body: JSON.stringify({ feedback_status, feedback_notes }),
    });
  },

  async updateSpeaker(
    insightId: string,
    speaker?: string,
    speaker_type?: 'cap8' | 'client' | 'unknown'
  ): Promise<Insight> {
    return fetchAPI(`/insights/${insightId}/speaker`, {
      method: 'PUT',
      body: JSON.stringify({ speaker, speaker_type }),
    });
  },

  async getInsightStats(): Promise<{
    total: number;
    by_module: Array<{ module_touched: string; count: number }>;
    by_urgency: Array<{ urgency: string; count: number }>;
    by_feedback: Array<{ feedback_status: string; count: number }>;
  }> {
    return fetchAPI('/insights/stats');
  },

  async getCapabilityTags(): Promise<Array<{ capability_tag: string; count: number }>> {
    return fetchAPI('/insights/tags');
  },

  // Query
  async executeQuery(
    query_type: 'point' | 'filtered' | 'large_scope',
    filters: InsightFilters,
    question?: string,
    conversationHistory?: ConversationTurn[]
  ): Promise<QueryResult | { insights: Insight[]; total: number }> {
    return fetchAPI('/query', {
      method: 'POST',
      body: JSON.stringify({ query_type, filters, question, conversation_history: conversationHistory }),
    });
  },

  /**
   * Streaming variant of executeQuery (SSE). Calls handlers as the server
   * emits pipeline stages, answer tokens, and the final structured result.
   * Resolves when the stream ends; throws on transport-level failure before
   * any data arrives (callers can fall back to executeQuery).
   */
  async executeQueryStream(
    filters: InsightFilters,
    question: string,
    conversationHistory: ConversationTurn[],
    handlers: QueryStreamHandlers
  ): Promise<void> {
    const auth = getAuth();
    if (!auth) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/query/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth}`,
      },
      body: JSON.stringify({
        query_type: 'filtered',
        filters,
        question,
        conversation_history: conversationHistory,
      }),
    });

    if (response.status === 401) {
      redirectToLogin();
      throw new Error('Session expired');
    }

    if (!response.ok || !response.body) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((error as { error?: string }).error || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const dispatch = (eventName: string, payload: string) => {
      let data: unknown;
      try {
        data = JSON.parse(payload);
      } catch {
        return;
      }
      if (eventName === 'stage') handlers.onStage?.(data as QueryStage);
      else if (eventName === 'delta') handlers.onDelta?.((data as { text: string }).text);
      else if (eventName === 'done') handlers.onDone?.(data as QueryResult);
      else if (eventName === 'error') handlers.onError?.((data as { message: string }).message);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        let eventName = 'message';
        const dataLines: string[] = [];
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length > 0) {
          dispatch(eventName, dataLines.join('\n'));
        }
      }
    }
  },

  async listQueryHistory(limit?: number): Promise<QueryHistory[]> {
    const query = limit ? `?limit=${limit}` : '';
    return fetchAPI(`/query/history${query}`);
  },

  async getQueryHistory(queryId: string): Promise<QueryHistory> {
    return fetchAPI(`/query/history/${queryId}`);
  },

  // Commitments
  async listCommitments(
    filters: CommitmentFilters
  ): Promise<{ commitments: Commitment[]; total: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchAPI(`/commitments${query ? `?${query}` : ''}`);
  },

  async getCommitment(id: string): Promise<Commitment> {
    return fetchAPI(`/commitments/${id}`);
  },

  async getCommitmentsByTranscript(transcriptId: string): Promise<Commitment[]> {
    return fetchAPI(`/transcripts/${transcriptId}/commitments`);
  },

  async updateCommitmentStatus(
    commitmentId: string,
    status: CommitmentStatus,
    notes?: string
  ): Promise<Commitment> {
    return fetchAPI(`/commitments/${commitmentId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  },

  async updateCommitmentFeedback(
    commitmentId: string,
    feedback_status: string,
    feedback_notes?: string
  ): Promise<Commitment> {
    return fetchAPI(`/commitments/${commitmentId}/feedback`, {
      method: 'PUT',
      body: JSON.stringify({ feedback_status, feedback_notes }),
    });
  },

  async getCommitmentStats(): Promise<CommitmentStats> {
    return fetchAPI('/commitments/stats');
  },

  async getAttentionNeeded(): Promise<AttentionNeeded> {
    return fetchAPI('/commitments/attention');
  },

  // Standing Items
  async listStandingItems(clientId?: string): Promise<StandingItem[]> {
    const query = clientId ? `?client_id=${clientId}` : '';
    return fetchAPI(`/standing-items${query}`);
  },

  async getStandingItem(id: string): Promise<StandingItem> {
    return fetchAPI(`/standing-items/${id}`);
  },

  async createStandingItem(data: {
    client_id: string;
    item_text: string;
    cadence: StandingItemCadence;
    notes?: string;
  }): Promise<StandingItem> {
    return fetchAPI('/standing-items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateStandingItem(
    standingItemId: string,
    data: {
      item_text?: string;
      cadence?: StandingItemCadence;
      notes?: string;
      status?: StandingItemStatus;
    }
  ): Promise<StandingItem> {
    return fetchAPI(`/standing-items/${standingItemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteStandingItem(standingItemId: string): Promise<void> {
    return fetchAPI(`/standing-items/${standingItemId}`, {
      method: 'DELETE',
    });
  },

  async markStandingItemComplete(standingItemId: string): Promise<StandingItem> {
    return fetchAPI(`/standing-items/${standingItemId}/complete`, {
      method: 'POST',
    });
  },

  async getStandingItemsDue(): Promise<StandingItem[]> {
    return fetchAPI('/standing-items/due');
  },

  // Client Summary (combined CRM view)
  async getClientSummary(clientId: string): Promise<ClientSummary> {
    return fetchAPI(`/clients/${clientId}/summary`);
  },

  // Client Status History (for funnel analytics)
  async getClientStatusHistory(clientId: string): Promise<ClientStatusHistory[]> {
    return fetchAPI(`/clients/${clientId}/status-history`);
  },

  // Roadmap Items
  async listRoadmapItems(filters: RoadmapFilters = {}): Promise<{ roadmap_items: RoadmapItemWithCounts[]; total: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchAPI(`/roadmap${query ? `?${query}` : ''}`);
  },

  async getRoadmapItem(roadmapItemId: string): Promise<RoadmapItem> {
    return fetchAPI(`/roadmap/${roadmapItemId}`);
  },

  async createRoadmapItem(data: {
    title: string;
    description?: string;
    module_touched: RoadmapModule;
    work_type: RoadmapWorkType;
    horizon: RoadmapHorizon;
    status?: RoadmapStatus;
    target_ship_date?: string;
    owner?: string;
    notes?: string;
    created_by?: string;
  }): Promise<RoadmapItem> {
    return fetchAPI('/roadmap', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateRoadmapItem(
    roadmapItemId: string,
    data: Partial<{
      title: string;
      description: string;
      module_touched: RoadmapModule;
      work_type: RoadmapWorkType;
      horizon: RoadmapHorizon;
      status: RoadmapStatus;
      target_ship_date: string;
      actual_ship_date: string;
      owner: string;
      notes: string;
    }>
  ): Promise<RoadmapItem> {
    return fetchAPI(`/roadmap/${roadmapItemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async markRoadmapItemShipped(roadmapItemId: string, actualShipDate: string): Promise<RoadmapItem> {
    return fetchAPI(`/roadmap/${roadmapItemId}/ship`, {
      method: 'POST',
      body: JSON.stringify({ actual_ship_date: actualShipDate }),
    });
  },

  async deleteRoadmapItem(roadmapItemId: string): Promise<void> {
    return fetchAPI(`/roadmap/${roadmapItemId}`, {
      method: 'DELETE',
    });
  },

  async getRoadmapOwners(): Promise<Array<{ owner: string }>> {
    return fetchAPI('/roadmap/owners');
  },

  // Loop View
  async getLoopView(roadmapItemId: string): Promise<LoopViewData> {
    return fetchAPI(`/roadmap/${roadmapItemId}/loop`);
  },

  // Flywheel Summary
  async getFlywheelSummary(timeFilter?: 'all' | '12_months' | '6_months' | 'quarter'): Promise<FlywheelSummaryData> {
    const query = timeFilter ? `?time_filter=${timeFilter}` : '';
    return fetchAPI(`/roadmap/flywheel${query}`);
  },

  // Insight-Roadmap Links
  async linkInsightToRoadmap(
    roadmapItemId: string,
    insightId: string,
    data?: { linked_by?: string; link_notes?: string }
  ): Promise<InsightRoadmapLink> {
    return fetchAPI(`/roadmap/${roadmapItemId}/insights`, {
      method: 'POST',
      body: JSON.stringify({ insight_id: insightId, ...data }),
    });
  },

  async unlinkInsightFromRoadmap(roadmapItemId: string, insightId: string): Promise<void> {
    return fetchAPI(`/roadmap/${roadmapItemId}/insights/${insightId}`, {
      method: 'DELETE',
    });
  },

  async getInsightRoadmapLinks(insightId: string): Promise<RoadmapItem[]> {
    return fetchAPI(`/insights/${insightId}/roadmap-links`);
  },

  async getRoadmapInsightLinks(roadmapItemId: string): Promise<Array<Insight & { client_name: string; link_notes: string | null }>> {
    return fetchAPI(`/roadmap/${roadmapItemId}/insights`);
  },

  async searchInsightsForLinking(
    roadmapItemId: string,
    filters: {
      client_id?: string;
      date_start?: string;
      date_end?: string;
      capability_tag?: string;
      search_text?: string;
      limit?: number;
    }
  ): Promise<Array<Insight & { client_name: string }>> {
    const searchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchAPI(`/roadmap/${roadmapItemId}/search-insights${query ? `?${query}` : ''}`);
  },

  // Client Notifications
  async addClientNotification(
    roadmapItemId: string,
    data: {
      client_id: string;
      notified_at: string;
      notified_by?: string;
      channel?: NotificationChannel;
      notes?: string;
    }
  ): Promise<ClientNotification> {
    return fetchAPI(`/roadmap/${roadmapItemId}/notifications`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getRoadmapNotifications(roadmapItemId: string): Promise<Array<ClientNotification & { client_name: string }>> {
    return fetchAPI(`/roadmap/${roadmapItemId}/notifications`);
  },

  async deleteNotification(notificationId: string): Promise<void> {
    return fetchAPI(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  },

  // Client Adoption
  async updateClientAdoption(
    roadmapItemId: string,
    data: {
      client_id: string;
      adoption_status: AdoptionStatus;
      status_changed_by?: string;
      notes?: string;
    }
  ): Promise<ClientAdoption> {
    return fetchAPI(`/roadmap/${roadmapItemId}/adoption`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getRoadmapAdoption(roadmapItemId: string): Promise<Array<ClientAdoption & { client_name: string }>> {
    return fetchAPI(`/roadmap/${roadmapItemId}/adoption`);
  },

  async addClientToLoop(
    roadmapItemId: string,
    data: {
      client_id: string;
      added_by?: string;
      notes?: string;
    }
  ): Promise<ClientAdoption & { client_name: string }> {
    return fetchAPI(`/roadmap/${roadmapItemId}/clients`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Mood Grid
  async getMoodGrid(params?: {
    client_ids?: string[];
    limit?: number;
  }): Promise<MoodGridResponse> {
    const searchParams = new URLSearchParams();
    if (params?.client_ids?.length) {
      searchParams.set('client_ids', params.client_ids.join(','));
    }
    if (params?.limit) {
      searchParams.set('limit', params.limit.toString());
    }
    const query = searchParams.toString();
    return fetchAPI(`/mood-grid${query ? `?${query}` : ''}`);
  },

  // Client Briefs
  async getClientBrief(
    clientId: string,
    briefType: ClientBriefType,
    forceRefresh?: boolean
  ): Promise<ClientBrief> {
    const query = forceRefresh ? '?refresh=true' : '';
    return fetchAPI(`/clients/${clientId}/brief/${briefType}${query}`);
  },

  // Market Mood Periods
  async listMarketMoodPeriods(): Promise<{ periods: MarketMoodPeriod[]; total: number }> {
    return fetchAPI('/settings/market-mood');
  },

  async getCurrentMarketMoodPeriod(): Promise<{
    current_period: MarketMoodPeriod | null;
    as_of_date: string;
  }> {
    return fetchAPI('/settings/market-mood/current');
  },

  async getMarketMoodPeriod(periodId: string): Promise<MarketMoodPeriod> {
    return fetchAPI(`/settings/market-mood/${periodId}`);
  },

  async createMarketMoodPeriod(data: {
    start_date: string;
    end_date?: string | null;
    mood: SentimentMarket;
    notes?: string;
    set_by: string;
  }): Promise<MarketMoodPeriod> {
    return fetchAPI('/settings/market-mood', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateMarketMoodPeriod(
    periodId: string,
    data: {
      start_date?: string;
      end_date?: string | null;
      mood?: SentimentMarket;
      notes?: string | null;
    }
  ): Promise<MarketMoodPeriod> {
    return fetchAPI(`/settings/market-mood/${periodId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteMarketMoodPeriod(periodId: string): Promise<{ deleted: boolean; period_id: string }> {
    return fetchAPI(`/settings/market-mood/${periodId}`, {
      method: 'DELETE',
    });
  },

  async runMarketMoodTagging(): Promise<MarketMoodTaggingResult> {
    return fetchAPI('/settings/market-mood/retag', {
      method: 'POST',
    });
  },

  async getMarketMoodStats(): Promise<MarketMoodStats> {
    return fetchAPI('/settings/market-mood/stats');
  },

  // Strategy Demand
  async listStrategyMentions(
    filters: StrategyMentionFilters = {}
  ): Promise<{ mentions: StrategyMention[]; total: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchAPI(`/strategy-mentions${query ? `?${query}` : ''}`);
  },

  async getStrategyMention(mentionId: string): Promise<StrategyMention> {
    return fetchAPI(`/strategy-mentions/${mentionId}`);
  },

  async getStrategyMentionsByTranscript(transcriptId: string): Promise<StrategyMention[]> {
    return fetchAPI(`/transcripts/${transcriptId}/strategy-mentions`);
  },

  async updateStrategyMentionFeedback(
    mentionId: string,
    feedbackStatus: FeedbackStatus,
    feedbackNotes?: string
  ): Promise<{ success: boolean }> {
    return fetchAPI(`/strategy-mentions/${mentionId}/feedback`, {
      method: 'PUT',
      body: JSON.stringify({ feedback_status: feedbackStatus, feedback_notes: feedbackNotes }),
    });
  },

  async getStrategyMentionStats(filters?: {
    client_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<StrategyMentionStats> {
    const searchParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, value);
        }
      });
    }
    const query = searchParams.toString();
    return fetchAPI(`/strategy-mentions/stats${query ? `?${query}` : ''}`);
  },

  async getDemandOverTime(params?: {
    group_by?: 'month' | 'quarter';
    client_id?: string;
    date_from?: string;
    date_to?: string;
    market_mood?: MarketMood;
  }): Promise<DemandOverTimePoint[]> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return fetchAPI(`/strategy-mentions/demand-over-time${query ? `?${query}` : ''}`);
  },

  async getClientStrategyLandscape(clientId: string): Promise<ClientStrategyLandscape> {
    return fetchAPI(`/clients/${clientId}/strategy-landscape`);
  },

  async backfillStrategyDemand(): Promise<{ message: string }> {
    return fetchAPI('/admin/backfill-strategy-demand', {
      method: 'POST',
    });
  },

  async getBadges(): Promise<BadgeCounts> {
    return fetchAPI('/badges');
  },

  async getTriageQueue(params: { branch?: Branch; limit?: number; offset?: number } = {}): Promise<TriageResponse> {
    const searchParams = new URLSearchParams();
    if (params.branch) searchParams.set('branch', params.branch);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return fetchAPI(`/insights/triage${query ? `?${query}` : ''}`);
  },

  async setInsightDisposition(
    id: string,
    body: { disposition: 'rejected' | 'pending'; rejection_reason?: string }
  ): Promise<Insight> {
    return fetchAPI(`/insights/${id}/disposition`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async updateInsightBranch(id: string, branch: Branch | null): Promise<Insight> {
    return fetchAPI(`/insights/${id}/branch`, {
      method: 'PUT',
      body: JSON.stringify({ branch }),
    });
  },

  async getPulseMarket(): Promise<PulseMarketData> {
    return fetchAPI('/pulse/market');
  },

  async getPulseRelationships(): Promise<PulseRelationshipsData> {
    return fetchAPI('/pulse/relationships');
  },

  async ackClientSentiment(clientId: string): Promise<{ success: boolean }> {
    return fetchAPI(`/pulse/relationships/${clientId}/ack`, {
      method: 'POST',
    });
  },

  async getClientAsks(clientId: string): Promise<ClientAsksResponse> {
    return fetchAPI(`/clients/${clientId}/asks`);
  },

  async adminBackfillBranch(): Promise<{ queued_insight_batches: number; roadmap_items_classified: number; insights_marked_linked: number }> {
    return fetchAPI('/admin/backfill-branch', {
      method: 'POST',
    });
  },

  // Articles / Market Knowledge
  async listArticles(
    filters: ArticleFilters = {}
  ): Promise<{ articles: Article[]; total: number; limit: number; offset: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchAPI(`/articles${query ? `?${query}` : ''}`);
  },

  async getArticle(articleId: string): Promise<{ article: Article; extraction: ArticleExtraction | null }> {
    return fetchAPI(`/articles/${articleId}`);
  },

  async extractArticlePasteMetadata(sourcePaste: string): Promise<ArticlePasteMetadata> {
    return fetchAPI('/articles/extract-metadata', {
      method: 'POST',
      body: JSON.stringify({ source_paste: sourcePaste }),
    });
  },

  async submitArticle(data: {
    source_paste: string;
    headline?: string;
    source_publication?: string;
    author?: string;
    publication_date?: string;
    url?: string;
    document_type?: ArticleDocumentType;
    curator_note?: string;
  }): Promise<{ article_id: string; status: string; message: string }> {
    return fetchAPI('/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateArticle(
    articleId: string,
    updates: Partial<{
      headline: string;
      source_publication: string;
      author: string;
      publication_date: string;
      url: string;
      document_type: ArticleDocumentType;
      curator_note: string;
    }>
  ): Promise<{ message: string }> {
    return fetchAPI(`/articles/${articleId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async updateArticleExtractionFeedback(
    articleId: string,
    feedbackStatus: string,
    feedbackNotes?: string
  ): Promise<{ message: string }> {
    return fetchAPI(`/articles/${articleId}/feedback`, {
      method: 'PUT',
      body: JSON.stringify({ feedback_status: feedbackStatus, feedback_notes: feedbackNotes }),
    });
  },

  async deleteArticle(articleId: string): Promise<{ message: string }> {
    return fetchAPI(`/articles/${articleId}`, {
      method: 'DELETE',
    });
  },

  async listSourcePublications(): Promise<string[]> {
    return fetchAPI('/articles/sources');
  },

  async backfillArticleExtractions(): Promise<{ message: string; article_count: number }> {
    return fetchAPI('/admin/backfill-articles', {
      method: 'POST',
    });
  },

  // Transcript Query (Q&A over transcripts)
  async executeTranscriptQuery(
    request: TranscriptQueryRequest
  ): Promise<TranscriptQueryResponse> {
    return fetchAPI('/transcript-query', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async listTranscriptQueryHistory(limit?: number): Promise<TranscriptQueryHistory[]> {
    const query = limit ? `?limit=${limit}` : '';
    return fetchAPI(`/transcript-query/history${query}`);
  },

  async getTranscriptQueryHistory(queryId: string): Promise<TranscriptQueryHistory> {
    return fetchAPI(`/transcript-query/history/${queryId}`);
  },

  // User Management (admin only)
  async listUsers(): Promise<{ users: User[]; total: number }> {
    return fetchAPI('/users');
  },

  async getUser(userId: string): Promise<User> {
    return fetchAPI(`/users/${userId}`);
  },

  async createUser(data: {
    user_id: string;
    name: string;
    email: string;
    role: UserRole;
    password: string; // Sent as plaintext over HTTPS - server hashes with PBKDF2
  }): Promise<{ success: boolean; user_id: string; message: string }> {
    return fetchAPI('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateUser(
    userId: string,
    data: {
      name?: string;
      email?: string;
      role?: UserRole;
    }
  ): Promise<User> {
    return fetchAPI(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteUser(userId: string): Promise<{ success: boolean; deleted: string; message: string }> {
    return fetchAPI(`/users/${userId}`, {
      method: 'DELETE',
    });
  },

  async resetUserPassword(userId: string, password: string): Promise<{ success: boolean }> {
    // Sent as plaintext over HTTPS - the server hashes with PBKDF2 and
    // revokes the target user's existing sessions.
    return fetchAPI(`/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },
};
